import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import _, { set } from 'lodash';
import React, { useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useDispatch, useSelector } from 'react-redux';

import { productSlice } from '@sophocles/features/warehouse/products/slice';

function ProductAutocomplete({
  customerCatalog,
  name,
  size,
  error,
  helperText,
  required,
  fullWidth,
  disabled,
  value,
  onChange,
  onBlur,
  filter = null,
  label = 'Product',
  sx = [],
}) {
  const dispatch = useDispatch();

  const isLoading = useSelector(state => productSlice.selectors.selectIsLoading(state));
  const [inputValue, setInputValue] = useState('');

  const products = useSelector(state => productSlice.selectors.selectAllEntities(state));

  let filteredProducts = products;
  if (filter != null) {
    filteredProducts = products.filter(filter);
  }

  const customerProductIds = customerCatalog?.map(cp => cp.product.id) ?? [];

  const groupedProducts = [
    ...(customerCatalog?.map(cp => ({
      ...cp.product,
      item_price: cp.price ?? cp.product?.item_price,
      group: 'Customer Products',
    })) ?? []),
    ...filteredProducts
      .filter(p => !customerProductIds.includes(p.id))
      .map(p => ({
        ...p,
        group: 'All Products',
      })),
  ];

  return (
    <Autocomplete
      name={name}
      fullWidth={fullWidth}
      disablePortal
      loading={isLoading}
      value={filteredProducts.find(product => product.id === value) || null}
      inputValue={inputValue}
      disabled={disabled}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={product => `${product.item_part_num}: ${product.item_name} - ${product.item_description}`}
      groupBy={option => option.group || ''}
      options={groupedProducts}
      sx={[...(Array.isArray(sx) ? sx : [sx])]}
      onBlur={onBlur}
      onChange={(e, opt) => {
        onChange(opt);
      }}
      onInputChange={(e, value) => {
        setInputValue(value);
      }}
      renderOption={(props, product) => (
        <Box
          {...props}
          component="li"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start!important',
            padding: '8px',
            borderBottom: '1px solid var(--border-default)',
          }}
        >
          <Typography variant="body1" component="div">
            <Highlighter
              searchWords={[inputValue]}
              autoEscape
              textToHighlight={`${product.item_part_num}: ${product.item_name}`}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            <Highlighter searchWords={[inputValue]} autoEscape textToHighlight={product.item_description} />
          </Typography>
          <Typography variant="caption" color="primary" sx={{ mt: 0.5 }}>
            {`Qty: ${product.qty_available}`}
          </Typography>
        </Box>
      )}
      renderInput={params => (
        <TextField
          id={`${name}-autocomplete-input`}
          {...params}
          required={required || false}
          fullWidth={fullWidth || false}
          name={name}
          size={size || 'small'}
          label={label}
          error={error}
          helperText={helperText}
          sx={[...(Array.isArray(sx) ? sx : [sx])]}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default ProductAutocomplete;
